/**
 * Motor de análisis de conducción — Pipeline V5, implementación 5-ts.3.
 *
 * @ported-from GuardIA1224/guardia-platform-sites@776c789 src/analysis/driving-analysis-v1.ts
 * @parity      tests/golden/parity.test.ts compara esta implementación contra
 *              el código original ejecutándose lado a lado. Si diverge, falla.
 *
 * Es una función pura: mismos registros y mismo período, mismo resultado, en
 * cualquier máquina y cualquier día. Sin reloj, sin azar, sin red, sin LLM.
 *
 * DIVERGENCIA-04 frente a V5: el perfil entra como parámetro en vez de estar
 * clavado en el módulo, para poder aplicar el que corresponde a la cadencia del
 * equipo (ver `profiles.ts` y ADR-007). Omitirlo usa `LAB_PROFILE`, que es lo
 * que hace producción — y es como lo llama la suite de paridad.
 *
 * El recorrido del cálculo, de arriba abajo:
 *
 *   registros crudos
 *     → recorte al período pedido
 *     → colapso de timestamps duplicados        (una posición por instante)
 *     → banderas de calidad                     (¿este punto es creíble?)
 *     → tramos entre puntos consecutivos        (distancia, tiempo, velocidad)
 *     → viajes                                  (cortados por huecos > 5 min)
 *     → candidatos                              (aceleración, frenada, giro, velocidad)
 *     → episodios                               (candidatos contiguos, fusionados)
 *     → severidad                               (bandas del perfil)
 *     → tasas normalizadas                      (por 100 km y por hora)
 */

import { PROVIDER_MARKED_INVALID, type GuardiaHistoryRecord } from "../contracts/telemetry.ts";
import { ANALYSIS_TIMEZONE, parseAnalysisPeriod, parseStrictIsoTimestamp } from "./analysis-period.ts";
import { LAB_PROFILE, REPEATED_FIX_MINIMUM_SPEED_KMH, type SeverityBand } from "./lab-profile.ts";
import type { AnalysisProfile } from "./profiles.ts";

/*
 * 5-ts.3 (ADR-010): las posiciones que el proveedor marca inválidas y las que
 * repiten la fijación anterior en plena marcha dejan de entrar al cálculo. Las
 * cifras cambian con los mismos datos y el mismo perfil, así que la versión
 * cambia con ellas: va en la firma de cada informe.
 */
export const DRIVING_ANALYSIS_VERSION = "5-ts.3" as const;
export const PIPELINE_VERSION = "V5" as const;

export { LAB_PROFILE };

// ---------------------------------------------------------------------------
// Tipos internos
// ---------------------------------------------------------------------------

type Classification = "VALID" | "DISCARDED" | "INCONCLUSIVE";

type CandidateType =
  | "acceleration_candidate"
  | "braking_candidate"
  | "left_turn_candidate"
  | "right_turn_candidate"
  | "elevated_speed_candidate";

type Severity = "low" | "moderate" | "high" | "critical" | "inconclusive";

type Position = GuardiaHistoryRecord & {
  timeMs: number | null;
  qualityFlags: string[];
  classification: Classification;
  inputIndexes: number[];
};

type Segment = {
  index: number;
  previous: number;
  current: number;
  deviceId: string;
  startMs: number | null;
  endMs: number | null;
  startLat: number | null;
  startLon: number | null;
  endLat: number | null;
  endLon: number | null;
  elapsedSeconds: number | null;
  distanceMeters: number | null;
  implicitSpeedKmh: number | null;
  providerSpeed: number | null;
  eligible: boolean;
  exclusionReasons: string[];
};

type Trip = {
  index: number;
  startPosition: number;
  endPosition: number;
  segmentIndexes: number[];
  durationSeconds: number | null;
  distanceMeters: number;
  eligible: boolean;
  exclusionReasons: string[];
};

type Candidate = {
  type: CandidateType;
  source: "longitudinal" | "turn" | "elevated_speed";
  segmentIndexes: number[];
  tripIndex: number | null;
  value: number;
  referenceSpeedKmh?: number;
  excessPercentage?: number;
};

type Episode = {
  type: CandidateType;
  source: Candidate["source"];
  tripIndex: number | null;
  segmentIndexes: number[];
  candidateCount: number;
  severity: Severity;
};

export type DrivingAnalysisInput = {
  records: GuardiaHistoryRecord[];
  received: number;
  requestedFrom: string;
  requestedTo: string;
};

// ---------------------------------------------------------------------------
// Clasificación de banderas de calidad
// ---------------------------------------------------------------------------

/** Sospechosas: el punto puede ser real, pero no se confía para calcular. */
const SUSPICIOUS_FLAGS = new Set([
  "impossible_position_jump",
  "ignition_off_while_moving",
  "speed_without_valid_position",
  "inconsistent_distance",
  "non_monotonic_time",
  "provider_geometry_speed_mismatch",
  "isolated_zero_speed_motion_dropout",
]);

/** Inválidas: el punto no sirve de ninguna manera. */
const INVALID_FLAGS = new Set([
  "provider_marked_invalid",
  "missing_coordinates",
  "coordinates_out_of_range",
  // ADR-010: no es una medición, es la anterior repetida.
  "repeated_fix_while_moving",
]);

/**
 * Puntos que no son una fijación nueva (ADR-010): la posición que el proveedor
 * marca inválida y la fijación anterior repetida en plena marcha.
 *
 * No se bloquean sus tramos: se SALTAN. El tramo va del punto anterior al
 * siguiente, y los dos se juzgan entre sí. Bloquear —lo que hace el resto de
 * banderas— perdía el trecho real entre el anterior y el siguiente: en el
 * recorrido del caso, una quinta parte de los kilómetros, y con ella una tasa
 * por 100 km inflada en la misma proporción.
 */
const GHOST_FLAGS = new Set(["provider_marked_invalid", "repeated_fix_while_moving"]);

const isGhost = (position: Position) => position.qualityFlags.some((flag) => GHOST_FLAGS.has(flag));

/**
 * Bloqueantes de tramo. El ORDEN de este Set decide el orden en que aparecen
 * las razones de exclusión, y ese orden sale al informe: no reordenar.
 */
const BLOCKING_FLAGS = new Set([
  "provider_marked_invalid",
  "missing_coordinates",
  "coordinates_out_of_range",
  "missing_time",
  "non_monotonic_time",
  "duplicate_exact",
  "impossible_position_jump",
  "speed_without_valid_position",
  "provider_geometry_speed_mismatch",
  "isolated_zero_speed_motion_dropout",
]);

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

const round = (value: number): number => Math.round(value * 100) / 100;

const finite = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined && Number.isFinite(value);

const unique = <T,>(values: T[]): T[] => [...new Set(values)];

export function parseGuardiaTimestamp(value: string | null): number | null {
  return parseStrictIsoTimestamp(value);
}

function hasCoordinates(position: Position | GuardiaHistoryRecord): boolean {
  return (
    finite(position.latitude) &&
    finite(position.longitude) &&
    position.latitude >= -90 &&
    position.latitude <= 90 &&
    position.longitude >= -180 &&
    position.longitude <= 180
  );
}

/** Distancia sobre la esfera, en metros. Radio medio de la Tierra IUGG. */
function haversine(a: Position, b: Position): number {
  const radius = 6371008.8;
  const lat1 = (a.latitude as number) * (Math.PI / 180);
  const lat2 = (b.latitude as number) * (Math.PI / 180);
  const deltaLat = ((b.latitude as number) - (a.latitude as number)) * (Math.PI / 180);
  const deltaLon = ((b.longitude as number) - (a.longitude as number)) * (Math.PI / 180);
  const h =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;
  return 2 * radius * Math.asin(Math.sqrt(h));
}

/** Cuántos campos trae el registro. Desempata cuál copia conservar. */
function completeness(record: GuardiaHistoryRecord): number {
  return [
    record.latitude,
    record.longitude,
    record.altitude_m,
    record.speed_kmh,
    record.heading,
    record.distance,
    record.ignition,
    record.motion,
    record.satellites,
    record.battery,
    record.alarm,
    record.event_type,
    record.total_distance,
  ].filter((value) => value !== null).length;
}

/**
 * Cuando dos registros comparten instante, este criterio decide cuál sobrevive.
 * Es un orden total y declarado: sin él, el resultado dependería del orden en
 * que WOX devolvió las páginas (constitución, Art. 1).
 */
function preferred(a: GuardiaHistoryRecord, b: GuardiaHistoryRecord): GuardiaHistoryRecord {
  const rank = (record: GuardiaHistoryRecord) => [
    record.validation_status === "VALID" ? 2 : record.validation_status === "INCONCLUSIVE" ? 1 : 0,
    completeness(record),
    parseGuardiaTimestamp(record.server_timestamp) ?? -Infinity,
    Number(record.source_record_id) || -Infinity,
  ];
  const rankA = rank(a);
  const rankB = rank(b);
  for (let i = 0; i < rankA.length; i++) {
    if (rankA[i] !== rankB[i]) return (rankB[i] as number) > (rankA[i] as number) ? b : a;
  }
  return a;
}

// ---------------------------------------------------------------------------
// Paso 1 — Colapso de duplicados por instante
// ---------------------------------------------------------------------------

function normalizeAndCollapse(
  records: GuardiaHistoryRecord[],
  profile: AnalysisProfile,
): {
  positions: Position[];
  duplicates: number;
} {
  const groups = new Map<string, Array<{ record: GuardiaHistoryRecord; index: number }>>();
  const order: Array<string | { record: GuardiaHistoryRecord; index: number }> = [];

  records.forEach((record, index) => {
    const time = parseGuardiaTimestamp(record.timestamp);
    // Sin instante interpretable no se puede agrupar: conserva su lugar suelto.
    if (time === null) {
      order.push({ record, index });
      return;
    }
    const key = `${record.device_id}|${time}`;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push({ record, index });
  });

  let duplicates = 0;

  const base = order.map((item): Position => {
    if (typeof item !== "string") {
      return {
        ...item.record,
        timeMs: parseGuardiaTimestamp(item.record.timestamp),
        qualityFlags: [],
        classification: "VALID",
        inputIndexes: [item.index],
      };
    }

    const group = groups.get(item)!;
    duplicates += group.length - 1;

    let selected = group[0]!.record;
    for (const candidate of group.slice(1)) selected = preferred(selected, candidate.record);

    const flags = group.length > 1 ? ["duplicate_timestamp_collapsed"] : [];
    const first = group[0]!.record;
    if (group.some((entry) => entry.record.speed_kmh !== first.speed_kmh)) {
      flags.push("duplicate_timestamp_speed_conflict");
    }
    if (group.some((entry) => entry.record.heading !== first.heading)) {
      flags.push("duplicate_timestamp_course_conflict");
    }
    if (group.some((entry) => entry.record.validation_status !== first.validation_status)) {
      flags.push("duplicate_timestamp_valid_conflict");
    }

    return {
      ...selected,
      timeMs: parseGuardiaTimestamp(selected.timestamp),
      qualityFlags: flags,
      classification: "VALID",
      inputIndexes: group.map((entry) => entry.index),
    };
  });

  return { positions: evaluateQuality(base, profile), duplicates };
}

// ---------------------------------------------------------------------------
// Paso 2 — Banderas de calidad por punto
// ---------------------------------------------------------------------------

function evaluateQuality(positions: Position[], profile: AnalysisProfile): Position[] {
  const { quality } = profile;

  /*
   * Primero se marcan los puntos que no son una fijación nueva (ADR-010), y
   * después cada punto se juzga contra sus vecinos REALES. Si el punto
   * siguiente a una repetida se comparara con ella, heredaría un salto que no
   * dio —la repetida está atrás en el camino— y su tramo quedaría bloqueado
   * por una falla ajena. Sin repetidas ni inválidas, los vecinos son los de
   * siempre y nada cambia.
   */
  const ghost: boolean[] = [];
  let lastReal: Position | undefined;
  for (const position of positions) {
    const isGhostPoint =
      position.validation_reason === PROVIDER_MARKED_INVALID ||
      (lastReal !== undefined && isRepeatedFixWhileMoving(lastReal, position, profile));
    ghost.push(isGhostPoint);
    if (!isGhostPoint) lastReal = position;
  }
  const previousReal = (index: number): Position | undefined => {
    for (let i = index - 1; i >= 0; i--) if (!ghost[i]) return positions[i];
    return undefined;
  };
  const followingReal = (index: number): Position | undefined => {
    for (let i = index + 1; i < positions.length; i++) if (!ghost[i]) return positions[i];
    return undefined;
  };

  return positions.map((position, index) => {
    const previous = previousReal(index);
    const following = followingReal(index);
    const flags = [...position.qualityFlags];
    const add = (flag: string) => {
      if (!flags.includes(flag)) flags.push(flag);
    };

    if (position.validation_reason === "COORDINATES_INVALID") add("coordinates_out_of_range");
    // El proveedor dijo que esta posición no es una fijación válida (ADR-010).
    // La bandera existía desde el V5 original y nunca se encendía.
    if (position.validation_reason === PROVIDER_MARKED_INVALID) add("provider_marked_invalid");
    if (
      position.validation_reason === "COORDINATES_MISSING" ||
      position.latitude === null ||
      position.longitude === null
    ) {
      add("missing_coordinates");
    }
    if (position.timeMs === null) add("missing_time");
    if (!hasCoordinates(position) && position.latitude !== null && position.longitude !== null) {
      add("coordinates_out_of_range");
    }

    // El tiempo tiene que avanzar. Si retrocede o se repite, algo está mal.
    if (
      previous &&
      position.timeMs !== null &&
      previous.timeMs !== null &&
      position.timeMs <= previous.timeMs
    ) {
      add("non_monotonic_time");
    }

    if (finite(position.satellites) && position.satellites < quality.minimumSatelliteCount) {
      add("low_satellite_count");
    }
    if (position.ignition === false && (position.motion === true || (position.speed_kmh ?? 0) > 0)) {
      add("ignition_off_while_moving");
    }
    if ((position.speed_kmh ?? 0) > 0 && !hasCoordinates(position)) {
      add("speed_without_valid_position");
    }
    if (finite(position.speed_kmh) && position.speed_kmh > quality.maximumImplicitSpeedKmh) {
      add("implausible_speed");
    }

    // El odómetro no baja. Se tolera un margen por el redondeo del proveedor.
    const previousTotal = previous?.total_distance;
    if (
      (position.distance !== null && position.distance < 0) ||
      (position.total_distance !== null && position.total_distance < 0) ||
      (previousTotal !== null &&
        previousTotal !== undefined &&
        position.total_distance !== null &&
        position.total_distance < previousTotal - quality.totalDistanceTolerance)
    ) {
      add("inconsistent_distance");
    }

    // ¿La geometría y la velocidad reportada cuentan la misma historia?
    if (
      previous &&
      previous.device_id === position.device_id &&
      previous.timeMs !== null &&
      position.timeMs !== null &&
      position.timeMs > previous.timeMs &&
      hasCoordinates(previous) &&
      hasCoordinates(position)
    ) {
      const distance = haversine(previous, position);
      const implicitSpeed = distance / ((position.timeMs - previous.timeMs) / 3600000) / 1000;
      if (implicitSpeed > quality.maximumImplicitSpeedKmh) add("impossible_position_jump");
      if (
        finite(position.speed_kmh) &&
        Math.abs(implicitSpeed - position.speed_kmh) >
          quality.maximumProviderGeometrySpeedDifferenceKmh
      ) {
        add("provider_geometry_speed_mismatch");
      }
    }

    if (previous && following && isDropout(previous, position, following, profile)) {
      add("isolated_zero_speed_motion_dropout");
    }

    if (ghost[index] && position.validation_reason !== PROVIDER_MARKED_INVALID) {
      add("repeated_fix_while_moving");
    }

    const set = new Set(flags);
    const classification: Classification = [...set].some((flag) => INVALID_FLAGS.has(flag))
      ? "DISCARDED"
      : [...set].some((flag) => SUSPICIOUS_FLAGS.has(flag))
        ? "INCONCLUSIVE"
        : set.size
          ? "INCONCLUSIVE"
          : "VALID";

    return { ...position, qualityFlags: flags, classification };
  });
}

/**
 * Un mensaje que repite la fijación anterior en plena marcha (ADR-010).
 *
 * Varios equipos —se vio en el gt06 del 06/10/2026, con 13 en un recorrido de
 * 18 minutos— mandan paquetes de alarma o de estado que llevan la última
 * posición guardada con un instante nuevo y velocidad 0. El vehículo venía en
 * marcha, así que la coordenada idéntica no es una parada: es la misma lectura
 * repetida. Dejarla entrar fabrica un tramo a 0 km/h entre dos tramos a 30, y
 * el detector longitudinal lo cuenta como una frenada y una aceleración que no
 * existieron. En ese recorrido eran 7 episodios, todos falsos.
 *
 * El detector `isDropout` existía para esto, pero exige el campo `motion`, que
 * el histórico de WOX no trae. Esta regla no depende de él: mira solo la
 * coordenada, la velocidad del punto y la del anterior.
 *
 * Una parada de verdad no la dispara: frenar hasta 0 recorre metros, así que la
 * primera lectura detenida ya está en otra coordenada.
 *
 * El umbral es `quality.repeatedFixMinimumSpeedKmh`. Un perfil guardado antes
 * del ADR-010 no trae el campo y hereda el de la familia, encendido: guardar
 * umbrales no es pedir que entren las posiciones repetidas. Solo un 0 explícito
 * la apaga, y eso lo hace únicamente el perfil de laboratorio de la paridad.
 */
function isRepeatedFixWhileMoving(previous: Position, current: Position, profile: AnalysisProfile): boolean {
  const minimumSpeed =
    (profile.quality as { repeatedFixMinimumSpeedKmh?: number }).repeatedFixMinimumSpeedKmh ??
    REPEATED_FIX_MINIMUM_SPEED_KMH;
  if (!(minimumSpeed > 0)) return false;
  return (
    previous.device_id === current.device_id &&
    previous.timeMs !== null &&
    current.timeMs !== null &&
    current.timeMs > previous.timeMs &&
    hasCoordinates(previous) &&
    hasCoordinates(current) &&
    previous.latitude === current.latitude &&
    previous.longitude === current.longitude &&
    current.speed_kmh === 0 &&
    finite(previous.speed_kmh) &&
    previous.speed_kmh >= minimumSpeed
  );
}

/**
 * Detecta el falso "se detuvo" de un solo punto: el equipo reporta velocidad
 * cero y sin movimiento entre dos puntos que sí se movían, y la velocidad
 * implícita del puente entre vecinos confirma que nunca paró.
 */
function isDropout(a: Position, b: Position, c: Position, profile: AnalysisProfile): boolean {
  const { quality } = profile;

  if (a.device_id !== b.device_id || b.device_id !== c.device_id) return false;
  if (a.timeMs === null || b.timeMs === null || c.timeMs === null) return false;
  if (!hasCoordinates(a) || !hasCoordinates(b) || !hasCoordinates(c)) return false;
  if (b.speed_kmh !== 0 || b.motion !== false) return false;
  if (a.motion !== true || c.motion !== true) return false;
  if (!finite(a.speed_kmh) || !finite(c.speed_kmh)) return false;
  if (
    a.speed_kmh < quality.minimumContinuousMotionSpeedKmh ||
    c.speed_kmh < quality.minimumContinuousMotionSpeedKmh
  ) {
    return false;
  }

  const before = (b.timeMs - a.timeMs) / 1000;
  const after = (c.timeMs - b.timeMs) / 1000;
  if (before <= 0 || after <= 0) return false;
  if (
    before > quality.maximumIsolatedDropoutGapSeconds ||
    after > quality.maximumIsolatedDropoutGapSeconds
  ) {
    return false;
  }

  const bridgeSpeed = haversine(a, c) / ((c.timeMs - a.timeMs) / 3600000) / 1000;
  return (
    bridgeSpeed > 0 &&
    Math.abs(bridgeSpeed - (a.speed_kmh + c.speed_kmh) / 2) <=
      quality.maximumBridgeSpeedDifferenceKmh
  );
}

// ---------------------------------------------------------------------------
// Paso 3 — Tramos entre puntos consecutivos
// ---------------------------------------------------------------------------

function buildSegments(positions: Position[]): Segment[] {
  // Los puntos que no son una fijación nueva no abren ni cierran tramo
  // (ADR-010). Sin ellos, `real` son todos los índices y esto es el V5.
  const real = positions.flatMap((position, index) => (isGhost(position) ? [] : [index]));
  return real.slice(1).map((currentIndex, i) => {
    const previousIndex = real[i]!;
    const previous = positions[previousIndex]!;
    const current = positions[currentIndex]!;
    const reasons: string[] = [];

    if (previous.device_id !== current.device_id) reasons.push("different_device");

    let elapsed: number | null = null;
    if (previous.timeMs === null || current.timeMs === null) {
      reasons.push("missing_time");
    } else {
      elapsed = (current.timeMs - previous.timeMs) / 1000;
      if (elapsed === 0) reasons.push("zero_time_interval");
      else if (elapsed < 0) reasons.push("negative_time_interval");
    }

    const combined = unique([...previous.qualityFlags, ...current.qualityFlags]);
    for (const flag of BLOCKING_FLAGS) {
      // El desajuste geometría/velocidad solo bloquea si es del punto de llegada:
      // el del punto de partida ya bloqueó su propio tramo anterior.
      if (flag === "provider_geometry_speed_mismatch" && !current.qualityFlags.includes(flag)) {
        continue;
      }
      if (combined.includes(flag) && !reasons.includes(flag)) reasons.push(flag);
    }

    const distance =
      hasCoordinates(previous) && hasCoordinates(current) && previous.device_id === current.device_id
        ? haversine(previous, current)
        : null;
    const implicitSpeed =
      distance !== null && elapsed !== null && elapsed > 0
        ? distance / (elapsed / 3600) / 1000
        : null;

    return {
      index: i,
      previous: previousIndex,
      current: currentIndex,
      deviceId: current.device_id,
      startMs: previous.timeMs,
      endMs: current.timeMs,
      startLat: previous.latitude,
      startLon: previous.longitude,
      endLat: current.latitude,
      endLon: current.longitude,
      elapsedSeconds: elapsed,
      distanceMeters: distance,
      implicitSpeedKmh: implicitSpeed,
      providerSpeed: current.speed_kmh,
      eligible: reasons.length === 0,
      exclusionReasons: reasons,
    };
  });
}

const midpoint = (segment: Segment): number | null =>
  segment.startMs === null || segment.endMs === null ? null : (segment.startMs + segment.endMs) / 2;

/** Rumbo inicial del tramo, en grados desde el norte. */
function bearing(segment: Segment): number | null {
  if (
    !finite(segment.startLat) ||
    !finite(segment.startLon) ||
    !finite(segment.endLat) ||
    !finite(segment.endLon)
  ) {
    return null;
  }
  const lat1 = segment.startLat * (Math.PI / 180);
  const lat2 = segment.endLat * (Math.PI / 180);
  const deltaLon = (segment.endLon - segment.startLon) * (Math.PI / 180);
  const y = Math.sin(deltaLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Diferencia de rumbo con signo, en (-180, 180]. Positivo = a la derecha. */
function signedChange(from: number, to: number): number {
  const value = ((to - from + 180) % 360) - 180;
  return value <= -180 ? 180 : value;
}

// ---------------------------------------------------------------------------
// Paso 4 — Viajes
// ---------------------------------------------------------------------------

function segmentTrips(
  positions: Position[],
  segments: Segment[],
  profile: AnalysisProfile,
): Trip[] {
  if (!positions.length) return [];
  const { trips } = profile;

  // Un hueco largo, un salto atrás en el tiempo o un cambio de equipo cortan.
  const ranges: Array<[number, number]> = [];
  let start = 0;
  for (let i = 1; i < positions.length; i++) {
    const a = positions[i - 1]!;
    const b = positions[i]!;
    const gap = a.timeMs !== null && b.timeMs !== null ? (b.timeMs - a.timeMs) / 1000 : null;
    if (a.device_id !== b.device_id || gap === null || gap <= 0 || gap > trips.maximumGapSeconds) {
      ranges.push([start, i - 1]);
      start = i;
    }
  }
  ranges.push([start, positions.length - 1]);

  return ranges.map(([first, last], index) => {
    const indexes = segments
      .filter(
        (segment) =>
          segment.previous >= first &&
          segment.current <= last &&
          !segment.exclusionReasons.includes("different_device"),
      )
      .map((segment) => segment.index);

    const eligible = indexes.map((i) => segments[i]!).filter((segment) => segment.eligible);
    const distance = eligible.reduce((total, segment) => total + (segment.distanceMeters ?? 0), 0);
    const duration =
      positions[first]!.timeMs !== null && positions[last]!.timeMs !== null
        ? (positions[last]!.timeMs! - positions[first]!.timeMs!) / 1000
        : null;

    const reasons: string[] = [];
    if (duration === null) reasons.push("missing_duration");
    else if (duration < trips.minimumTripDurationSeconds) reasons.push("insufficient_duration");
    if (distance < trips.minimumTripDistanceMeters) reasons.push("insufficient_distance");
    if (eligible.length < trips.minimumEligibleSegments) {
      reasons.push("insufficient_eligible_segments");
    }

    return {
      index,
      startPosition: first,
      endPosition: last,
      segmentIndexes: indexes,
      durationSeconds: duration,
      distanceMeters: distance,
      eligible: reasons.length === 0,
      exclusionReasons: reasons,
    };
  });
}


/**
 * Rachas de velocidad sostenida por encima del límite.
 *
 * ## El error que esto corrige
 *
 * La regla anterior exigía que UN tramo durara los 5 s mínimos. Un tramo es el
 * espacio entre dos reportes, así que la condición se volvía imposible en
 * cuanto el equipo reportaba más seguido que cada 5 s: a 4 s de cadencia
 * ningún tramo llega nunca a 5, y el detector quedaba ciego. Cuanto MEJOR el
 * dato, menos veía — exactamente al revés de lo que debe pasar.
 *
 * Había un segundo error escondido en la misma regla: cada tramo por encima
 * del límite contaba como un episodio suelto. Un mismo trecho de autopista
 * daba el doble de episodios en un equipo de 5 s que en uno de 10 s, y esa
 * cuenta alimenta la tasa por 100 km y con ella el puntaje. Dos vehículos
 * conduciendo igual salían distintos por el equipo que llevaban puesto.
 *
 * ## Cómo funciona ahora
 *
 * Se acumulan tramos CONSECUTIVOS que van por encima del límite hasta reunir
 * la ventana pedida, y la racha entera es UN episodio. A 10 s basta un tramo;
 * a 4 s hacen falta dos; a 1 s, cinco. El criterio deja de depender de cada
 * cuánto reporte el equipo, que es el punto de todo este sistema (ADR-007).
 *
 * La velocidad que se publica es la de la racha completa —distancia total
 * sobre tiempo total—, no la del tramo más rápido: es la que de verdad se
 * sostuvo, y es la que un informe puede defender.
 *
 * ## Histéresis
 *
 * Un tercer error de la misma familia: sin histéresis la racha se corta en
 * cuanto la velocidad deja de superar el límite, así que un vehículo que
 * oscila alrededor de él abre una racha nueva EN CADA CRUCE. Medido en
 * producción con el límite en 80 km/h, un equipo de 10 s produjo 839 tramos
 * por encima en 3.104 km, en tiradas de autopista donde la velocidad ronda los
 * 80–86 km/h: un mismo trecho contado cientos de veces, y esa cuenta alimenta
 * la tasa por 100 km y con ella el puntaje.
 *
 * Con `operationalSpeedExitKmh` mayor que 0 la racha ABRE al superar
 * `operationalSpeedLimitKmh` y solo CIERRA al bajar por debajo del umbral de
 * salida; entre uno y otro sigue siendo el mismo episodio. En 0 —o si alguien
 * configura una salida por encima de la entrada, que no describe ninguna
 * histéresis— los dos umbrales son el mismo número y el comportamiento es el
 * de siempre, bit a bit.
 *
 * La regla vive dentro de `sustainedSpeedRunsOverLimit`, que es donde se ARMA
 * la racha, y no aquí: así la heredan por igual el informe y la vista previa
 * del laboratorio, que leen las rachas de esa misma función. Escribirla dos
 * veces es el error que este archivo ya cometió una vez.
 *
 * Consecuencia que conviene tener presente: con histéresis la racha puede
 * incluir tramos entre la salida y la entrada, así que la velocidad publicada
 * —y con ella el porcentaje de exceso— puede quedar POR DEBAJO del límite
 * operacional. No se recorta: es lo que se deduce de publicar la velocidad que
 * de verdad se sostuvo, y un episodio que promedia 79 km/h en una racha
 * abierta a 84 dice algo cierto sobre el trecho.
 */
function sustainedSpeedRuns(
  segments: Segment[],
  trips: Trip[],
  elevatedSpeed: AnalysisProfile["elevatedSpeed"],
): Candidate[] {
  return sustainedSpeedRunsOverLimit(segments, elevatedSpeed)
    .filter((run) => run.qualifies)
    .map((run): Candidate => {
      // La velocidad publicada es la de la racha completa, no la del tramo más
      // rápido: es la que de verdad se sostuvo.
      const speed = run.meters / 1000 / (run.seconds / 3600);
      const indexes = run.segments.map((segment) => segment.index);
      return {
        type: "elevated_speed_candidate",
        source: "elevated_speed",
        segmentIndexes: indexes,
        tripIndex: assignTrip(indexes, trips),
        value: speed,
        excessPercentage:
          (speed - elevatedSpeed.operationalSpeedLimitKmh) /
          (elevatedSpeed.operationalSpeedLimitKmh / 100),
      };
    });
}

/**
 * Una racha de tramos CONSECUTIVOS por encima del límite operacional, con su
 * duración y su distancia ya sumadas, y con el veredicto de si reúne o no la
 * ventana pedida.
 *
 * ## Por qué el agrupamiento vive aparte del detector
 *
 * Porque hay DOS lectores, y hasta ahora cada uno tenía su propia versión de la
 * regla. El informe agrupaba tramos en rachas; la vista previa del laboratorio
 * recorría los tramos por su cuenta y marcaba uno por uno los que iban por
 * encima del límite, sin enterarse nunca de que existían las rachas. Mismo
 * vehículo, mismo período y mismos umbrales daban 839 en pantalla y 242 en el
 * informe, y la perilla «velocidad sostenida durante» no movía la vista previa
 * porque la vista previa no la usaba.
 *
 * Es la MISMA clase de error que el proyecto ya había cerrado unificando
 * `applyThresholds` en `profiles.ts`: la regla escrita dos veces, se actualiza
 * una copia y no la otra. Es la segunda vez. Allá se unificó cómo se ARMA el
 * perfil; lo que faltaba unificar era cómo se CUENTAN los episodios.
 *
 * Devolver también las rachas que se quedaron cortas no es un lujo: es lo único
 * que permite a la vista previa explicar por qué un tramo que sí iba a 130 km/h
 * no produjo episodio. Sin eso, la pantalla solo puede callar o inventar.
 */
type SustainedSpeedRun = {
  segments: Segment[];
  seconds: number;
  meters: number;
  /** `true` si la racha reúne la ventana pedida: solo esas producen candidato. */
  qualifies: boolean;
};

function sustainedSpeedRunsOverLimit(
  segments: Segment[],
  elevatedSpeed: AnalysisProfile["elevatedSpeed"],
): SustainedSpeedRun[] {
  const runs: SustainedSpeedRun[] = [];
  let run: Segment[] = [];

  /*
   * Umbral de salida efectivo. Una salida en 0 apaga la histéresis; una salida
   * POR ENCIMA de la entrada tampoco describe una histéresis —cerraría la
   * racha antes de que hubiera podido abrirse—, así que se trata igual que
   * apagada en vez de inventarle un significado. Igualado a la entrada,
   * `continues` y `opens` son la misma condición y no cambia nada.
   */
  const exitKmh =
    elevatedSpeed.operationalSpeedExitKmh > 0 &&
    elevatedSpeed.operationalSpeedExitKmh <= elevatedSpeed.operationalSpeedLimitKmh
      ? elevatedSpeed.operationalSpeedExitKmh
      : elevatedSpeed.operationalSpeedLimitKmh;

  /** Lo que un tramo tiene que cumplir para poder formar parte de una racha. */
  const usable = (segment: Segment) =>
    segment.eligible &&
    finite(segment.implicitSpeedKmh) &&
    finite(segment.elapsedSeconds) &&
    finite(segment.distanceMeters) &&
    segment.distanceMeters >= elevatedSpeed.minimumSegmentDistanceMeters;

  /** Abre una racha nueva: hay que superar el límite de entrada. */
  const opens = (segment: Segment) =>
    usable(segment) && segment.implicitSpeedKmh! >= elevatedSpeed.operationalSpeedLimitKmh;

  /** Mantiene abierta una racha en curso: basta con no bajar de la salida. */
  const continues = (segment: Segment) => usable(segment) && segment.implicitSpeedKmh! >= exitKmh;

  const close = () => {
    if (!run.length) return;
    const seconds = run.reduce((total, segment) => total + segment.elapsedSeconds!, 0);
    const meters = run.reduce((total, segment) => total + segment.distanceMeters!, 0);
    runs.push({
      segments: run,
      seconds,
      meters,
      // La ventana se mide sobre la racha completa. Por debajo no se publica:
      // un pico de un instante no es velocidad sostenida.
      qualifies: seconds >= elevatedSpeed.sustainedWindowSeconds && seconds > 0,
    });
    run = [];
  };

  for (const segment of segments) {
    // Un salto en el índice significa que entre medio hubo un tramo que no
    // califica: la racha se corta ahí, no se salta el hueco.
    const previous = run[run.length - 1];
    if (previous && segment.index !== previous.index + 1) close();
    // Con la racha abierta manda el umbral de salida; para abrir una nueva
    // hace falta superar el de entrada. Sin histéresis los dos son el mismo.
    if (run.length ? continues(segment) : opens(segment)) run.push(segment);
    else close();
  }
  close();

  return runs;
}

/**
 * Aceleración lateral de un giro, en m/s².
 *
 * `a = v · ω`, con la velocidad en m/s y la velocidad angular en radianes por
 * segundo. Es la fuerza que el pasajero siente empujándolo contra la puerta, y
 * es lo que el manual de GuardIA usa como criterio — no la velocidad angular
 * suelta.
 *
 * La diferencia importa porque un umbral fijo de °/s se equivoca en las DOS
 * direcciones. A 15 km/h, 8 °/s son 0,58 m/s²: cuenta como giro cualquier
 * curva de calle. A 80 km/h bastan 8,8 °/s para llegar a 3,4 m/s², así que
 * exigir 17,5 °/s deja pasar sin marcar un giro de verdad peligroso en
 * carretera. El mismo número es demasiado abajo y demasiado poco arriba.
 */
export function lateralAccelerationMps2(speedKmh: number, yawRateDegreesPerSecond: number): number {
  return (speedKmh / 3.6) * Math.abs(yawRateDegreesPerSecond) * (Math.PI / 180);
}

/**
 * ¿El giro es lo bastante intenso para contarlo?
 *
 * Dos criterios y uno solo activo a la vez. Con `lateralAccelerationMps2` en 0
 * manda la velocidad angular fija, que es el comportamiento del Pipeline V5
 * original y por eso las pruebas de paridad siguen en verde. Con un valor
 * mayor que 0 manda la aceleración lateral del manual.
 */
function turnIsIntense(
  turns: { minimumYawRateDegreesPerSecond: number; lateralAccelerationMps2: number },
  yawRateDegreesPerSecond: number,
  referenceSpeedKmh: number,
): boolean {
  if (turns.lateralAccelerationMps2 > 0) {
    return (
      lateralAccelerationMps2(referenceSpeedKmh, yawRateDegreesPerSecond) >=
      turns.lateralAccelerationMps2
    );
  }
  return Math.abs(yawRateDegreesPerSecond) >= turns.minimumYawRateDegreesPerSecond;
}

/** Un episodio pertenece a un viaje solo si cae inequívocamente en uno. */
function assignTrip(indexes: number[], trips: Trip[]): number | null {
  const matches = trips.filter((trip) => indexes.every((i) => trip.segmentIndexes.includes(i)));
  return matches.length === 1 ? matches[0]!.index : null;
}

// ---------------------------------------------------------------------------
// Paso 5 — Candidatos
// ---------------------------------------------------------------------------

function detectCandidates(
  segments: Segment[],
  trips: Trip[],
  profile: AnalysisProfile,
): Candidate[] {
  const { longitudinal, turns, elevatedSpeed } = profile;
  const out: Candidate[] = [];

  for (let i = 1; i < segments.length; i++) {
    const a = segments[i - 1]!;
    const b = segments[i]!;
    const midA = midpoint(a);
    const midB = midpoint(b);
    const elapsed = midA !== null && midB !== null ? (midB - midA) / 1000 : null;

    // Ambas familias exigen dos tramos elegibles, encadenados y del mismo equipo.
    const chained =
      a.eligible &&
      b.eligible &&
      a.deviceId === b.deviceId &&
      a.current === b.previous &&
      finite(a.implicitSpeedKmh) &&
      finite(b.implicitSpeedKmh) &&
      elapsed !== null &&
      elapsed > 0;

    // Aceleración y frenada: variación de velocidad entre los centros de dos tramos.
    if (chained && elapsed <= longitudinal.maximumMidpointGapSeconds) {
      const deltaKmh = b.implicitSpeedKmh! - a.implicitSpeedKmh!;
      const acceleration = deltaKmh / 3.6 / elapsed!;
      const type: CandidateType | null =
        acceleration >= longitudinal.accelerationThresholdMps2 &&
        deltaKmh >= longitudinal.minimumSpeedChangeKmh
          ? "acceleration_candidate"
          : acceleration <= longitudinal.brakingThresholdMps2 &&
              deltaKmh <= -longitudinal.minimumSpeedChangeKmh
            ? "braking_candidate"
            : null;
      if (type) {
        out.push({
          type,
          source: "longitudinal",
          segmentIndexes: [i - 1, i],
          tripIndex: assignTrip([i - 1, i], trips),
          value: acceleration,
        });
      }
    }

    // Giro: cambio de rumbo con recorrido suficiente para que el rumbo signifique algo.
    if (
      chained &&
      elapsed <= turns.maximumMidpointGapSeconds &&
      (a.distanceMeters ?? 0) >= turns.minimumSegmentDistanceMeters &&
      (b.distanceMeters ?? 0) >= turns.minimumSegmentDistanceMeters
    ) {
      const headingA = bearing(a);
      const headingB = bearing(b);
      if (headingA !== null && headingB !== null) {
        const change = signedChange(headingA, headingB);
        const yawRate = change / elapsed!;
        const reference = Math.min(a.implicitSpeedKmh!, b.implicitSpeedKmh!);
        // Un cambio de exactamente 180° es media vuelta o ruido de rumbo: no cuenta.
        if (
          Math.abs(change) !== 180 &&
          Math.abs(change) >= turns.minimumHeadingChangeDegrees &&
          turnIsIntense(turns, yawRate, reference) &&
          reference >= turns.minimumReferenceSpeedKmh
        ) {
          out.push({
            type: change > 0 ? "right_turn_candidate" : "left_turn_candidate",
            source: "turn",
            segmentIndexes: [i - 1, i],
            tripIndex: assignTrip([i - 1, i], trips),
            value: Math.abs(yawRate),
            referenceSpeedKmh: reference,
          });
        }
      }
    }
  }

  // Velocidad elevada: sostenida, no un pico de un instante.
  //
  // La histéresis (`operationalSpeedExitKmh`) vive dentro de las rachas, así
  // que solo tiene efecto por esta rama: con la ventana apagada cada tramo se
  // evalúa suelto y no hay racha que mantener abierta. Encender la salida sin
  // encender la ventana no cambia nada, y así lo dice la perilla en el
  // laboratorio.
  if (elevatedSpeed.sustainedWindowSeconds > 0) {
    out.push(...sustainedSpeedRuns(segments, trips, elevatedSpeed));
  } else {
    for (const segment of segments) {
      if (
        segment.eligible &&
        finite(segment.implicitSpeedKmh) &&
        finite(segment.elapsedSeconds) &&
        segment.elapsedSeconds >= elevatedSpeed.minimumSegmentDurationSeconds &&
        finite(segment.distanceMeters) &&
        segment.distanceMeters >= elevatedSpeed.minimumSegmentDistanceMeters &&
        segment.implicitSpeedKmh >= elevatedSpeed.operationalSpeedLimitKmh
      ) {
        out.push({
          type: "elevated_speed_candidate",
          source: "elevated_speed",
          segmentIndexes: [segment.index],
          tripIndex: assignTrip([segment.index], trips),
          value: segment.implicitSpeedKmh,
          excessPercentage:
            (segment.implicitSpeedKmh - elevatedSpeed.operationalSpeedLimitKmh) /
            (elevatedSpeed.operationalSpeedLimitKmh / 100),
        });
      }
    }
  }

  return out;
}

// ---------------------------------------------------------------------------
// Paso 6 — Severidad y consolidación
// ---------------------------------------------------------------------------

function band(value: number, thresholds: SeverityBand): Severity {
  if (value >= thresholds[2]) return "critical";
  if (value >= thresholds[1]) return "high";
  if (value >= thresholds[0]) return "moderate";
  return "low";
}

const SEVERITY_ORDER: Severity[] = ["low", "moderate", "high", "critical", "inconclusive"];

function severityOf(candidate: Candidate, profile: AnalysisProfile): Severity {
  const { severity } = profile;

  if (candidate.type === "acceleration_candidate") {
    return band(Math.abs(candidate.value), severity.acceleration);
  }
  if (candidate.type === "braking_candidate") {
    return band(Math.abs(candidate.value), severity.braking);
  }
  if (candidate.type === "elevated_speed_candidate") {
    return band(candidate.excessPercentage ?? NaN, severity.elevatedSpeedExcessPercent);
  }
  if (!finite(candidate.referenceSpeedKmh)) return "inconclusive";

  // En giros manda la más BAJA de las dos bandas: un giro cerrado a poca
  // velocidad no es lo mismo que uno cerrado rápido. Criterio conservador.
  const yawBand = band(candidate.value, severity.yawRate);
  const speedBand = band(candidate.referenceSpeedKmh, severity.turnReferenceSpeed);
  return SEVERITY_ORDER[
    Math.min(SEVERITY_ORDER.indexOf(yawBand), SEVERITY_ORDER.indexOf(speedBand))
  ]!;
}

/**
 * Fusiona candidatos contiguos del mismo tipo y viaje en un solo episodio.
 * Una frenada larga produce varios candidatos consecutivos; contarlos por
 * separado inflaría la cifra y haría incomparables dos vehículos con
 * frecuencias de reporte distintas.
 */
function consolidate(candidates: Candidate[], profile: AnalysisProfile): Episode[] {
  const sorted = [...candidates].sort(
    (a, b) => Math.min(...a.segmentIndexes) - Math.min(...b.segmentIndexes),
  );

  const groups: Candidate[][] = [];
  for (const candidate of sorted) {
    const prior = groups.at(-1);
    const last = prior?.at(-1);
    const contiguous =
      last &&
      candidate.tripIndex !== null &&
      candidate.tripIndex === last.tripIndex &&
      candidate.source === last.source &&
      candidate.type === last.type &&
      Math.min(...candidate.segmentIndexes) <= Math.max(...last.segmentIndexes) + 1;
    if (contiguous) prior!.push(candidate);
    else groups.push([candidate]);
  }

  return groups.map((group) => ({
    type: group[0]!.type,
    source: group[0]!.source,
    tripIndex: group[0]!.tripIndex,
    segmentIndexes: unique(group.flatMap((item) => item.segmentIndexes)).sort((a, b) => a - b),
    candidateCount: group.length,
    // La severidad del episodio es la del candidato más intenso que lo compone.
    severity: severityOf(
      group.reduce((best, item) => (Math.abs(item.value) > Math.abs(best.value) ? item : best)),
      profile,
    ),
  }));
}

// ---------------------------------------------------------------------------
// Salida
// ---------------------------------------------------------------------------

function exclusionReasonCounts(segments: Segment[]): Array<{ reason: string; count: number }> {
  const count = new Map<string, number>();
  segments
    .filter((segment) => !segment.eligible)
    .flatMap((segment) => segment.exclusionReasons)
    .forEach((reason) => count.set(reason, (count.get(reason) ?? 0) + 1));
  return [...count]
    .map(([reason, value]) => ({ reason, count: value }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
}

/**
 * Tasas normalizadas. Sin esto no se pueden comparar dos vehículos: uno de
 * 3.000 km siempre tendrá más episodios que uno de 200, y eso no dice nada.
 */
function rates(count: number, distanceMeters: number, durationSeconds: number) {
  return {
    per100Km: distanceMeters > 0 ? round((count / (distanceMeters / 1000)) * 100) : null,
    perHour: durationSeconds > 0 ? round(count / (durationSeconds / 3600)) : null,
  };
}

export function analyzeDrivingHistoryV5(
  input: DrivingAnalysisInput,
  profile: AnalysisProfile = LAB_PROFILE as AnalysisProfile,
) {
  const requested = parseAnalysisPeriod({
    fromIso: input.requestedFrom,
    toIso: input.requestedTo,
    timezone: ANALYSIS_TIMEZONE,
  });
  if (!requested) throw new Error("INVALID_ANALYSIS_PERIOD");

  // WOX a veces devuelve registros fuera del rango pedido. Se recortan y se
  // cuentan: el informe dice cuántos quedaron fuera en lugar de callarlo.
  let outsideRequestedPeriod = 0;
  const scopedRecords = input.records.filter((record) => {
    const time = parseGuardiaTimestamp(record.timestamp);
    if (time === null) return true;
    const inside = time >= requested.fromEpochMs && time <= requested.toEpochMs;
    if (!inside) outsideRequestedPeriod++;
    return inside;
  });

  const normalized = normalizeAndCollapse(scopedRecords, profile);
  const positions = normalized.positions;
  const series = buildSegments(positions);
  const trips = segmentTrips(positions, series, profile);
  const candidates = detectCandidates(series, trips, profile);
  const episodes = consolidate(candidates, profile);

  const valid = positions.filter((p) => p.classification === "VALID");
  const discarded = positions.filter((p) => p.classification === "DISCARDED");
  const inconclusive = positions.filter((p) => p.classification === "INCONCLUSIVE");
  const eligible = series.filter(
    (s) => s.eligible && finite(s.distanceMeters) && finite(s.elapsedSeconds) && s.elapsedSeconds > 0,
  );

  const distance = eligible.reduce((total, s) => total + s.distanceMeters!, 0);
  const duration = eligible.reduce((total, s) => total + s.elapsedSeconds!, 0);
  const times = valid.map((p) => p.timeMs).filter(finite);
  const speeds = valid.map((p) => p.speed_kmh).filter(finite);

  // Guardia de cordura: la exposición no puede superar el período pedido.
  // Si pasa, hay un error de cálculo y es mejor fallar que publicar la cifra.
  if (duration > requested.durationSeconds + 0.001) throw new Error("EXPOSURE_EXCEEDS_REQUESTED_PERIOD");

  const status: "VALID" | "PARTIAL" | "INSUFFICIENT_DATA" =
    !positions.length || !eligible.length
      ? "INSUFFICIENT_DATA"
      : discarded.length ||
          inconclusive.length ||
          series.some((s) => !s.eligible) ||
          outsideRequestedPeriod
        ? "PARTIAL"
        : "VALID";

  const severityCounts = { low: 0, moderate: 0, high: 0, critical: 0, inconclusive: 0 };
  episodes.forEach((episode) => severityCounts[episode.severity]++);
  const moderateOrHigher =
    severityCounts.moderate + severityCounts.high + severityCounts.critical;
  const highOrCritical = severityCounts.high + severityCounts.critical;

  const episodeTimeline = episodes.map((episode, index) => {
    const evidence = episode.segmentIndexes.map((i) => series[i]).filter(Boolean) as Segment[];
    const startTimes = evidence.map((item) => item.startMs).filter(finite);
    const endTimes = evidence.map((item) => item.endMs).filter(finite);
    return {
      id: index + 1,
      type: episode.type,
      severity: episode.severity,
      candidateCount: episode.candidateCount,
      segmentCount: episode.segmentIndexes.length,
      start: startTimes.length ? new Date(Math.min(...startTimes)).toISOString() : null,
      end: endTimes.length ? new Date(Math.max(...endTimes)).toISOString() : null,
    };
  });

  return {
    pipelineVersion: PIPELINE_VERSION,
    implementationVersion: DRIVING_ANALYSIS_VERSION,
    analysisVersion: DRIVING_ANALYSIS_VERSION,
    status,
    profile: { ...profile, label: "Perfil técnico de laboratorio / DRAFT", commercialRules: false },
    period: {
      requestedFrom: requested.fromIso,
      requestedTo: requested.toIso,
      timezone: requested.timezone,
      requestedDurationSeconds: requested.durationSeconds,
      firstValidSample: times.length ? new Date(Math.min(...times)).toISOString() : null,
      lastValidSample: times.length ? new Date(Math.max(...times)).toISOString() : null,
      observedDurationSeconds:
        times.length >= 2 ? round((Math.max(...times) - Math.min(...times)) / 1000) : null,
    },
    dataQuality: {
      received: input.received,
      normalized: positions.length,
      deduplicated: positions.length,
      duplicatesCollapsed: normalized.duplicates,
      outsideRequestedPeriod,
      valid: valid.length,
      discarded: discarded.length,
      inconclusive: inconclusive.length,
      eligibleSegments: eligible.length,
      excludedSegments: series.length - eligible.length,
      validPercent: positions.length ? round((valid.length / positions.length) * 100) : 0,
      topExclusionReasons: exclusionReasonCounts(series),
    },
    exposure: {
      eligibleDistanceMeters: round(distance),
      eligibleDistanceKm: round(distance / 1000),
      eligibleDurationSeconds: round(duration),
      eligibleDurationHours: round(duration / 3600),
      candidateTrips: trips.length,
      eligibleTrips: trips.filter((t) => t.eligible).length,
      excludedTrips: trips.filter((t) => !t.eligible).length,
      eligibleSegments: eligible.length,
      excludedSegments: series.length - eligible.length,
    },
    technicalDriving: {
      maximumValidProviderSpeedKmh: speeds.length ? Math.max(...speeds) : null,
      accelerationCandidates: candidates.filter((c) => c.type === "acceleration_candidate").length,
      brakingCandidates: candidates.filter((c) => c.type === "braking_candidate").length,
      turnCandidates: candidates.filter((c) => c.source === "turn").length,
      elevatedSpeedCandidates: candidates.filter((c) => c.type === "elevated_speed_candidate").length,
      totalCandidates: candidates.length,
      consolidatedEpisodes: episodes.length,
      severityCounts,
      episodes: episodeTimeline,
    },
    normalization: {
      episodes: rates(episodes.length, distance, duration),
      moderateOrHigher: rates(moderateOrHigher, distance, duration),
      highOrCritical: rates(highOrCritical, distance, duration),
      critical: rates(severityCounts.critical, distance, duration),
    },
    unsupportedMetrics: [
      { metric: "DriveScore", reason: "Fuera del Pipeline V5 técnico; no existe scoring comercial." },
      {
        metric: "legalSpeeding",
        reason: "El umbral de 120 km/h es operacional de laboratorio, no un límite legal de vía.",
      },
      {
        metric: "driverFacts",
        reason: "Los candidatos y episodios son señales técnicas, no hechos definitivos del conductor.",
      },
    ],
    trace: {
      timezone: ANALYSIS_TIMEZONE,
      inputFields: [
        "timestamp",
        "server_timestamp",
        "latitude",
        "longitude",
        "speed_kmh",
        "heading",
        "distance",
        "total_distance",
        "ignition",
        "motion",
        "satellites",
        "alarm",
        "event_type",
        "validation_status",
        "validation_reason",
      ],
      warnings: unique([
        ...(status !== "VALID" ? ["partial_or_insufficient_quality"] : []),
        ...(outsideRequestedPeriod ? ["records_outside_requested_period_excluded"] : []),
        ...(episodes.some((e) => e.tripIndex === null) ? ["unassigned_episodes_present"] : []),
      ]),
    },
  };
}

export type DrivingAnalysisV5 = ReturnType<typeof analyzeDrivingHistoryV5>;

// ---------------------------------------------------------------------------
// Medición cruda, para el laboratorio de calibración
// ---------------------------------------------------------------------------

/**
 * Los valores continuos de TODOS los pares elegibles, sin aplicar ningún umbral.
 *
 * El análisis normal responde «¿cuántos episodios hubo?». Esto responde algo
 * anterior y más importante: «¿cómo se distribuye de verdad la aceleración en
 * esta flota?». Sin eso, cualquier umbral es una opinión.
 *
 * Reutiliza exactamente las mismas funciones que el análisis —normalización,
 * tramos, elegibilidad— para que lo que mide el laboratorio sea lo mismo que
 * después va a evaluar el motor. Si midiera por otro camino, calibraría para un
 * mundo que no existe.
 */
export type CandidateMeasurements = {
  /** Aceleración con signo, m/s². Negativo es frenada. */
  longitudinalMps2: number[];
  /** Velocidad angular absoluta, °/s. */
  yawDegreesPerSecond: number[];
  /** Velocidad de referencia del giro, km/h. */
  turnReferenceSpeedKmh: number[];
  /**
   * Intervalo del par que produjo cada giro, s. Alineado índice a índice con
   * `yawDegreesPerSecond`.
   *
   * `midpointIntervalSeconds` NO sirve para eso: se empuja en todos los pares
   * encadenados, mientras que el giro se descarta cuando algún tramo es
   * demasiado corto o cuando el cambio de rumbo es de 180° exactos. Los dos
   * arreglos tienen longitudes distintas, y emparejarlos por índice mezcla el
   * intervalo de un par con la velocidad angular de otro.
   *
   * Hace falta para traducir el cambio de rumbo mínimo a °/s con el intervalo
   * de **ese** par y no con un percentil del conjunto: ver
   * `core/report/component-measurability.ts`.
   */
  turnIntervalSeconds: number[];
  /** Velocidad implícita de cada tramo elegible, km/h. */
  segmentSpeedKmh: number[];
  /** Intervalo entre centros de tramos consecutivos, s. */
  midpointIntervalSeconds: number[];
  eligibleSegments: number;
  eligiblePairs: number;
};

export function measureCandidateValues(
  input: DrivingAnalysisInput,
  profile: AnalysisProfile = LAB_PROFILE as AnalysisProfile,
): CandidateMeasurements {
  const requested = parseAnalysisPeriod({
    fromIso: input.requestedFrom,
    toIso: input.requestedTo,
    timezone: ANALYSIS_TIMEZONE,
  });
  if (!requested) throw new Error("INVALID_ANALYSIS_PERIOD");

  const scoped = input.records.filter((record) => {
    const time = parseGuardiaTimestamp(record.timestamp);
    if (time === null) return true;
    return time >= requested.fromEpochMs && time <= requested.toEpochMs;
  });

  const { positions } = normalizeAndCollapse(scoped, profile);
  const segments = buildSegments(positions);

  const out: CandidateMeasurements = {
    longitudinalMps2: [],
    yawDegreesPerSecond: [],
    turnReferenceSpeedKmh: [],
    turnIntervalSeconds: [],
    segmentSpeedKmh: [],
    midpointIntervalSeconds: [],
    eligibleSegments: 0,
    eligiblePairs: 0,
  };

  for (const segment of segments) {
    if (segment.eligible && finite(segment.implicitSpeedKmh)) {
      out.eligibleSegments++;
      out.segmentSpeedKmh.push(round(segment.implicitSpeedKmh));
    }
  }

  for (let i = 1; i < segments.length; i++) {
    const a = segments[i - 1]!;
    const b = segments[i]!;
    const midA = midpoint(a);
    const midB = midpoint(b);
    const elapsed = midA !== null && midB !== null ? (midB - midA) / 1000 : null;

    // Mismas condiciones de encadenamiento que el detector, SIN los umbrales.
    if (
      !a.eligible ||
      !b.eligible ||
      a.deviceId !== b.deviceId ||
      a.current !== b.previous ||
      !finite(a.implicitSpeedKmh) ||
      !finite(b.implicitSpeedKmh) ||
      elapsed === null ||
      elapsed <= 0
    ) {
      continue;
    }

    out.eligiblePairs++;
    out.midpointIntervalSeconds.push(round(elapsed));
    out.longitudinalMps2.push(round((b.implicitSpeedKmh - a.implicitSpeedKmh) / 3.6 / elapsed));

    if (
      (a.distanceMeters ?? 0) >= profile.turns.minimumSegmentDistanceMeters &&
      (b.distanceMeters ?? 0) >= profile.turns.minimumSegmentDistanceMeters
    ) {
      const headingA = bearing(a);
      const headingB = bearing(b);
      if (headingA !== null && headingB !== null) {
        const change = signedChange(headingA, headingB);
        // La media vuelta exacta se excluye igual que en el detector: es ruido
        // de rumbo, y colarla aquí desplazaría la distribución hacia arriba.
        if (Math.abs(change) !== 180) {
          out.yawDegreesPerSecond.push(round(Math.abs(change / elapsed)));
          out.turnReferenceSpeedKmh.push(round(Math.min(a.implicitSpeedKmh, b.implicitSpeedKmh)));
          out.turnIntervalSeconds.push(round(elapsed));
        }
      }
    }
  }

  return out;
}

// ---------------------------------------------------------------------------
// Inspección tramo a tramo, para el laboratorio de calibración
// ---------------------------------------------------------------------------

/**
 * El motor responde «cuántos episodios hubo». Esto responde algo distinto:
 * «para CADA tramo, qué se midió, qué se disparó, y si no se disparó, cuál de
 * las condiciones lo detuvo».
 *
 * Es lo que hace posible calibrar sin adivinar. Bajar un umbral a ciegas hasta
 * que aparezcan episodios es cambiar un número arbitrario por otro; ver que
 * cuarenta maniobras se quedaron a 0,42 m/s² de un umbral de 0,5 es información.
 *
 * Vive dentro de este archivo, y no en un módulo aparte, por una razón que
 * importa: no reimplementa nada. Llama a `detectCandidates` y a `consolidate`
 * —el camino exacto del informe— y de ahí saca tanto los conteos como el
 * veredicto de cada fila. Una segunda implementación «equivalente» calibraría
 * para un motor que no existe: eso es justo lo que pasó con la velocidad
 * elevada, que sí estaba reimplementada aquí y contaba tramo por tramo en vez
 * de racha por racha (839 en pantalla contra 242 en el informe).
 *
 * La tabla tramo a tramo sigue existiendo, porque es lo que permite explicar
 * por qué algo NO disparó. Pero la explicación se DERIVA del detector; nunca se
 * calcula en paralelo a él. Las pruebas `tests/golden/inspector-parity.test.ts`
 * y `paridad-vista-previa-informe.test.ts` exigen que los conteos coincidan.
 */
export type PairVerdict = {
  triggered: boolean;
  /** Qué disparó, si disparó. */
  type: string | null;
  /**
   * Cuál condición lo detuvo, en palabras y con los dos números comparados.
   * `null` si disparó.
   */
  blockedBy: string | null;
};

export type InspectedSegment = {
  index: number;
  startMs: number | null;
  endMs: number | null;
  elapsedSeconds: number | null;
  distanceMeters: number | null;
  implicitSpeedKmh: number | null;
  providerSpeedKmh: number | null;
  startLat: number | null;
  startLon: number | null;
  endLat: number | null;
  endLon: number | null;
  eligible: boolean;
  exclusionReasons: string[];
  elevatedSpeed: PairVerdict & { excessPercent: number | null };
};

export type InspectedPair = {
  index: number;
  atMs: number | null;
  chained: boolean;
  midpointGapSeconds: number | null;
  speedFromKmh: number | null;
  speedToKmh: number | null;
  deltaSpeedKmh: number | null;
  accelerationMps2: number | null;
  longitudinal: PairVerdict;
  headingChangeDegrees: number | null;
  yawRateDegreesPerSecond: number | null;
  turnReferenceSpeedKmh: number | null;
  distanceAMeters: number | null;
  distanceBMeters: number | null;
  turn: PairVerdict;
};

export type DrivingInspection = {
  profileId: string;
  regime: string;
  calibration: string;
  segments: InspectedSegment[];
  pairs: InspectedPair[];
  /**
   * EPISODIOS por familia: exactamente lo que el informe publica, ya
   * consolidado. Es el número que se mira para elegir un umbral, así que tiene
   * que ser el mismo que va a salir impreso.
   */
  counts: {
    acceleration: number;
    braking: number;
    turn: number;
    elevatedSpeed: number;
    total: number;
  };
  /**
   * CANDIDATOS por familia, antes de consolidar. No es lo que se publica, pero
   * es la única cifra monótona en los umbrales: bajar un umbral siempre produce
   * más candidatos, mientras que puede producir MENOS episodios si dos que
   * estaban separados pasan a tocarse y se funden en uno. Separarlos evita que
   * el laboratorio confunda «detecté menos» con «hubo menos».
   */
  candidateCounts: {
    acceleration: number;
    braking: number;
    turn: number;
    elevatedSpeed: number;
    total: number;
  };
  eligibleDistanceKm: number;
  eligibleDurationHours: number;
  eligibleSegments: number;
  ratesPer100Km: {
    acceleration: number | null;
    braking: number | null;
    turn: number | null;
    elevatedSpeed: number | null;
    total: number | null;
  };
};

const n2 = (value: number) => Math.round(value * 100) / 100;

export function inspectDriving(
  input: DrivingAnalysisInput,
  profile: AnalysisProfile = LAB_PROFILE as AnalysisProfile,
): DrivingInspection {
  const requested = parseAnalysisPeriod({
    fromIso: input.requestedFrom,
    toIso: input.requestedTo,
    timezone: ANALYSIS_TIMEZONE,
  });
  if (!requested) throw new Error("INVALID_ANALYSIS_PERIOD");

  const scoped = input.records.filter((record) => {
    const time = parseGuardiaTimestamp(record.timestamp);
    if (time === null) return true;
    return time >= requested.fromEpochMs && time <= requested.toEpochMs;
  });

  const { positions } = normalizeAndCollapse(scoped, profile);
  const segments = buildSegments(positions);
  const { longitudinal, turns, elevatedSpeed } = profile;

  // El camino del informe, entero y sin atajos: los mismos viajes, los mismos
  // candidatos y la misma consolidación que `analyzeDrivingHistoryV5`. Todo lo
  // que esta función cuenta o explica sale de aquí.
  const trips = segmentTrips(positions, segments, profile);
  const candidates = detectCandidates(segments, trips, profile);
  const episodes = consolidate(candidates, profile);

  // Índices para poder responder «¿este tramo, este par, disparó?» sin volver a
  // leer los umbrales. Un par `i` corresponde a los tramos `[i - 1, i]`, así que
  // el mayor de los dos índices identifica al par sin ambigüedad.
  const elevatedSegments = new Set<number>();
  const longitudinalByPair = new Map<number, Candidate>();
  const turnByPair = new Map<number, Candidate>();
  for (const candidate of candidates) {
    if (candidate.source === "elevated_speed") {
      for (const index of candidate.segmentIndexes) elevatedSegments.add(index);
    } else if (candidate.source === "longitudinal") {
      longitudinalByPair.set(Math.max(...candidate.segmentIndexes), candidate);
    } else {
      turnByPair.set(Math.max(...candidate.segmentIndexes), candidate);
    }
  }

  // Las rachas, incluidas las que se quedaron cortas. Son la única forma de
  // decir «iba a 130, pero su racha duró 4 s y la ventana pide 10» en vez de
  // dejar la casilla del motivo vacía.
  const runBySegment = new Map<number, SustainedSpeedRun>();
  if (elevatedSpeed.sustainedWindowSeconds > 0) {
    for (const run of sustainedSpeedRunsOverLimit(segments, elevatedSpeed)) {
      for (const segment of run.segments) runBySegment.set(segment.index, run);
    }
  }

  /**
   * Por qué este tramo no entró en ningún episodio de velocidad elevada.
   *
   * Las condiciones se leen en el mismo orden en que el detector las aplica, y
   * la rama depende de si la ventana de velocidad sostenida está encendida. Con
   * la ventana apagada manda la regla vieja —un tramo suelto que dure
   * `minimumSegmentDurationSeconds`—. Con la ventana encendida lo que se mide
   * es la racha completa, así que la duración de ESTE tramo no bloquea nada:
   * decir «duró 2 s, menos de los 5 mínimos» cuando manda la racha mandaría a
   * mover la perilla equivocada, que es exactamente el daño que hace una
   * explicación calculada aparte del detector.
   */
  const explainElevatedSpeed = (segment: Segment): string => {
    const speed = segment.implicitSpeedKmh;
    const seconds = segment.elapsedSeconds;
    const meters = segment.distanceMeters;

    if (!segment.eligible) return "el tramo no es elegible";
    if (!finite(speed)) return "sin velocidad implícita";
    if (!finite(seconds)) return "sin duración medible";
    if (
      elevatedSpeed.sustainedWindowSeconds <= 0 &&
      seconds < elevatedSpeed.minimumSegmentDurationSeconds
    ) {
      return `duró ${n2(seconds)} s, menos de los ${elevatedSpeed.minimumSegmentDurationSeconds} s mínimos`;
    }
    if (!finite(meters) || meters < elevatedSpeed.minimumSegmentDistanceMeters)
      return `recorrió ${n2(meters ?? 0)} m, menos de los ${elevatedSpeed.minimumSegmentDistanceMeters} m mínimos`;
    if (speed < elevatedSpeed.operationalSpeedLimitKmh)
      return `${n2(speed)} km/h, por debajo del umbral de ${elevatedSpeed.operationalSpeedLimitKmh} km/h`;

    const run = runBySegment.get(segment.index);
    if (run) {
      return (
        `pertenece a una racha de ${n2(run.seconds)} s, por debajo de la ventana de ` +
        `${elevatedSpeed.sustainedWindowSeconds} s`
      );
    }
    return "el detector no lo tomó como velocidad elevada";
  };

  const inspectedSegments: InspectedSegment[] = segments.map((segment) => {
    const speed = segment.implicitSpeedKmh;
    // El veredicto lo da el detector. Aquí solo se traduce a palabras.
    const triggered = elevatedSegments.has(segment.index);
    const blockedBy = triggered ? null : explainElevatedSpeed(segment);

    return {
      index: segment.index,
      startMs: segment.startMs,
      endMs: segment.endMs,
      elapsedSeconds: segment.elapsedSeconds === null ? null : n2(segment.elapsedSeconds),
      distanceMeters: segment.distanceMeters === null ? null : n2(segment.distanceMeters),
      implicitSpeedKmh: speed === null ? null : n2(speed),
      providerSpeedKmh: segment.providerSpeed,
      startLat: segment.startLat,
      startLon: segment.startLon,
      endLat: segment.endLat,
      endLon: segment.endLon,
      eligible: segment.eligible,
      exclusionReasons: segment.exclusionReasons,
      elevatedSpeed: {
        triggered,
        type: triggered ? "elevated_speed" : null,
        blockedBy,
        // El exceso de la FILA es el de este tramo, no el de la racha: la fila
        // es una medición del tramo y publicar aquí la media de la racha
        // dejaría el exceso peleado con la velocidad de al lado. El número que
        // el informe publica es el de la racha, y sale del candidato.
        excessPercent:
          triggered && finite(speed)
            ? n2((speed - elevatedSpeed.operationalSpeedLimitKmh) / (elevatedSpeed.operationalSpeedLimitKmh / 100))
            : null,
      },
    };
  });

  const pairs: InspectedPair[] = [];

  for (let i = 1; i < segments.length; i++) {
    const a = segments[i - 1]!;
    const b = segments[i]!;
    const midA = midpoint(a);
    const midB = midpoint(b);
    const elapsed = midA !== null && midB !== null ? (midB - midA) / 1000 : null;

    // Misma condición de encadenamiento que `detectCandidates`, textual.
    const chained =
      a.eligible &&
      b.eligible &&
      a.deviceId === b.deviceId &&
      a.current === b.previous &&
      finite(a.implicitSpeedKmh) &&
      finite(b.implicitSpeedKmh) &&
      elapsed !== null &&
      elapsed > 0;

    const deltaKmh = chained ? b.implicitSpeedKmh! - a.implicitSpeedKmh! : null;
    const acceleration = chained && deltaKmh !== null ? deltaKmh / 3.6 / elapsed! : null;

    // Quién disparó lo dice el detector; el motivo de los que no, esta función.
    const longCandidate = longitudinalByPair.get(i);
    const long: PairVerdict = {
      triggered: longCandidate !== undefined,
      type: longCandidate
        ? longCandidate.type === "acceleration_candidate"
          ? "acceleration"
          : "braking"
        : null,
      blockedBy: null,
    };
    if (!longCandidate) {
      if (!chained) long.blockedBy = "los dos tramos no encadenan";
      else if (elapsed! > longitudinal.maximumMidpointGapSeconds)
        long.blockedBy = `hay ${n2(elapsed!)} s entre los centros, más de los ${longitudinal.maximumMidpointGapSeconds} s máximos`;
      else if (acceleration! > 0)
        long.blockedBy =
          acceleration! < longitudinal.accelerationThresholdMps2
            ? `aceleró ${n2(acceleration!)} m/s², bajo el umbral de ${longitudinal.accelerationThresholdMps2}`
            : `subió ${n2(deltaKmh!)} km/h, menos del mínimo de ${longitudinal.minimumSpeedChangeKmh}`;
      else
        long.blockedBy =
          acceleration! > longitudinal.brakingThresholdMps2
            ? `frenó ${n2(acceleration!)} m/s², sobre el umbral de ${longitudinal.brakingThresholdMps2}`
            : `bajó ${n2(deltaKmh!)} km/h, menos del mínimo de ${longitudinal.minimumSpeedChangeKmh}`;
    }

    const headingA = bearing(a);
    const headingB = bearing(b);
    const change = chained && headingA !== null && headingB !== null ? signedChange(headingA, headingB) : null;
    const yawRate = change !== null && elapsed ? change / elapsed : null;
    const reference = chained ? Math.min(a.implicitSpeedKmh!, b.implicitSpeedKmh!) : null;

    const turnCandidate = turnByPair.get(i);
    const turn: PairVerdict = {
      triggered: turnCandidate !== undefined,
      type: turnCandidate
        ? turnCandidate.type === "right_turn_candidate"
          ? "right_turn"
          : "left_turn"
        : null,
      blockedBy: null,
    };
    if (!turnCandidate) {
      if (!chained) turn.blockedBy = "los dos tramos no encadenan";
      else if (elapsed! > turns.maximumMidpointGapSeconds)
        turn.blockedBy = `hay ${n2(elapsed!)} s entre los centros, más de los ${turns.maximumMidpointGapSeconds} s máximos`;
      else if ((a.distanceMeters ?? 0) < turns.minimumSegmentDistanceMeters || (b.distanceMeters ?? 0) < turns.minimumSegmentDistanceMeters)
        turn.blockedBy = `algún tramo recorrió menos de los ${turns.minimumSegmentDistanceMeters} m mínimos para que el rumbo signifique algo`;
      else if (change === null) turn.blockedBy = "no se pudo calcular el rumbo";
      else if (Math.abs(change) === 180) turn.blockedBy = "cambio de exactamente 180°: media vuelta o ruido de rumbo";
      else if (Math.abs(change) < turns.minimumHeadingChangeDegrees)
        turn.blockedBy = `giró ${n2(Math.abs(change))}°, menos de los ${turns.minimumHeadingChangeDegrees}° mínimos`;
      else if (!turnIsIntense(turns, yawRate!, reference!))
        // El motivo se dice en la unidad del criterio que está mandando. Decir
        // «bajo el umbral de °/s» cuando manda la aceleración lateral mandaría a
        // mover la perilla equivocada en el laboratorio.
        turn.blockedBy =
          turns.lateralAccelerationMps2 > 0
            ? `a ${n2(reference!)} km/h y ${n2(Math.abs(yawRate!))} °/s la aceleración lateral es ` +
              `${n2(lateralAccelerationMps2(reference!, yawRate!))} m/s², bajo los ` +
              `${turns.lateralAccelerationMps2} m/s² del manual`
            : `giró a ${n2(Math.abs(yawRate!))} °/s, bajo el umbral de ${turns.minimumYawRateDegreesPerSecond}`;
      else if (reference! < turns.minimumReferenceSpeedKmh)
        turn.blockedBy = `iba a ${n2(reference!)} km/h, bajo el mínimo de ${turns.minimumReferenceSpeedKmh} km/h`;
      else turn.blockedBy = "el detector no lo tomó como giro";
    }

    pairs.push({
      index: i,
      atMs: midB,
      chained,
      midpointGapSeconds: elapsed === null ? null : n2(elapsed),
      speedFromKmh: finite(a.implicitSpeedKmh) ? n2(a.implicitSpeedKmh) : null,
      speedToKmh: finite(b.implicitSpeedKmh) ? n2(b.implicitSpeedKmh) : null,
      deltaSpeedKmh: deltaKmh === null ? null : n2(deltaKmh),
      accelerationMps2: acceleration === null ? null : Math.round(acceleration * 1000) / 1000,
      longitudinal: long,
      headingChangeDegrees: change === null ? null : n2(change),
      yawRateDegreesPerSecond: yawRate === null ? null : Math.round(yawRate * 1000) / 1000,
      turnReferenceSpeedKmh: reference === null ? null : n2(reference),
      distanceAMeters: a.distanceMeters === null ? null : n2(a.distanceMeters),
      distanceBMeters: b.distanceMeters === null ? null : n2(b.distanceMeters),
      turn,
    });
  }

  const eligible = segments.filter(
    (s) => s.eligible && finite(s.distanceMeters) && finite(s.elapsedSeconds) && s.elapsedSeconds > 0,
  );
  const distanceKm = eligible.reduce((total, s) => total + s.distanceMeters!, 0) / 1000;
  const hours = eligible.reduce((total, s) => total + s.elapsedSeconds!, 0) / 3600;

  /*
   * Los conteos son EPISODIOS, no filas de la tabla, y salen de `consolidate`.
   *
   * Contar filas es lo que hacía esta función antes y es lo que produjo el
   * defecto: una racha de veinte tramos por encima del límite son veinte filas
   * y UN episodio, y el informe publica episodios. La tasa por 100 km que se
   * muestra al lado alimenta la misma decisión que la del informe, así que
   * tiene que estar hecha con el mismo número o la vista previa engaña.
   */
  const counts = {
    acceleration: episodes.filter((episode) => episode.type === "acceleration_candidate").length,
    braking: episodes.filter((episode) => episode.type === "braking_candidate").length,
    turn: episodes.filter((episode) => episode.source === "turn").length,
    elevatedSpeed: episodes.filter((episode) => episode.type === "elevated_speed_candidate").length,
    total: episodes.length,
  };

  const candidateCounts = {
    acceleration: candidates.filter((item) => item.type === "acceleration_candidate").length,
    braking: candidates.filter((item) => item.type === "braking_candidate").length,
    turn: candidates.filter((item) => item.source === "turn").length,
    elevatedSpeed: candidates.filter((item) => item.type === "elevated_speed_candidate").length,
    total: candidates.length,
  };

  const rate = (count: number) => (distanceKm > 0 ? n2((count / distanceKm) * 100) : null);

  return {
    profileId: profile.profileId,
    regime: profile.regime,
    calibration: profile.calibration,
    segments: inspectedSegments,
    pairs,
    counts,
    candidateCounts,
    eligibleDistanceKm: n2(distanceKm),
    eligibleDurationHours: n2(hours),
    eligibleSegments: eligible.length,
    ratesPer100Km: {
      acceleration: rate(counts.acceleration),
      braking: rate(counts.braking),
      turn: rate(counts.turn),
      elevatedSpeed: rate(counts.elevatedSpeed),
      total: rate(counts.total),
    },
  };
}
